<script setup lang="ts">
import { GameAssetTypes } from '@game/types'
import { BroadcastMessageType } from '@core/types'
import { computed, onMounted, ref, useCoreConfig, useFormat, useGame, useGameAssets, useGameJackpot, useHead, useI18n, useRoute, useRouter, useSharedBroadcast, useThemeIllustration, useThemeLogo, useTimeoutFn } from '#imports'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const { asMoney } = useFormat()
const { getGameBySeoName } = useGame()
const { firstAvailable } = useGameAssets()
const brandIcon = useThemeLogo('icon')
const brandHeroBackground = useThemeIllustration('heroBackground')
const { sendMessage } = useSharedBroadcast()

const slug = router?.currentRoute?.value?.params?.slug as string

const { data: game } = await getGameBySeoName(slug)

const { jackpot, superJackpot, jackpotTypeText } = useGameJackpot(game)

const { site } = useCoreConfig()

useHead({
    title: computed(() => game.value?.name ? `${game.value.name} | ${site.name}` : site.name),
})

const showAuth = ref(false)
const gameLogoFailed = ref(false)
const backgroundFailed = ref(false)

const gameLogo = computed(() => {
    if (gameLogoFailed.value || !game.value?.slug) return null

    return game.value.provider_slug?.toLowerCase() === 'magnet'
        ? firstAvailable(game.value, GameAssetTypes.FEATURED_LOGO, GameAssetTypes.LOGO)
        : firstAvailable(game.value, GameAssetTypes.LOGO)
})

const background = computed(() => backgroundFailed.value ? null : firstAvailable(game.value, GameAssetTypes.TILE))

const { start: revealAuth } = useTimeoutFn(() => {
    showAuth.value = !!route.query.auth
}, 2000, { immediate: false })

onMounted(() => {
    window.name = 'play'
    window.addEventListener('beforeunload',
        () => sendMessage({ type: BroadcastMessageType.GAME_TILE_STOP_LOADING, data: { slug } }),
        { once: true })

    revealAuth()
})
</script>

<template>
    <div class="entrance" data-test="game-entrance">
        <transition name="fade">
            <NuxtImg
                v-if="background"
                key="game-background"
                class="entrance__background"
                provider="cloudflare"
                preset="gameservice"
                :src="background"
                width="600"
                quality="70"
                fit="cover"
                :alt="t('game.entrance.backgroundAlt', { name: game?.name })"
                loading="eager"
                fetchpriority="high"
                :preload="{ fetchPriority: 'high' }"
                decoding="async"
                @error="backgroundFailed = true"
            />

            <NuxtImg
                v-else-if="brandHeroBackground"
                class="entrance__background entrance__fallback-background"
                provider="cloudflare"
                preset="local"
                :src="brandHeroBackground.src"
                width="600"
                alt=""
                loading="eager"
                fetchpriority="high"
                decoding="async"
            />
        </transition>

        <transition name="fade">
            <div v-if="game" class="entrance__game" data-test="game-entrance-game">
                <NuxtImg
                    v-if="gameLogo"
                    key="game-logo"
                    class="entrance__game-logo"
                    provider="cloudflare"
                    preset="gameservice"
                    :src="gameLogo"
                    width="300"
                    :alt="`${game?.name} logo`"
                    loading="eager"
                    fetchpriority="high"
                    :preload="{ fetchPriority: 'high' }"
                    decoding="async"
                    fit="contain"
                    @error="gameLogoFailed = true"
                />

                <NuxtImg
                    v-else-if="brandIcon"
                    class="entrance__fallback-logo"
                    provider="cloudflare"
                    preset="local"
                    :src="brandIcon.src"
                    :quality="brandIcon.quality"
                    width="200"
                    height="200"
                    alt=""
                    loading="eager"
                    fetchpriority="high"
                    decoding="async"
                />

                <div class="entrance__game-title">
                    <transition name="fade" mode="out-in">
                        <span v-if="showAuth" key="empty">
                            &nbsp;
                        </span>
                        <span v-else key="text">
                            {{ t('game.entrance.loading') }}
                        </span>
                    </transition>
                </div>

                <transition name="fade">
                    <div v-if="superJackpot || jackpot" class="entrance__game-jackpot">
                        <div class="entrance__game-jackpot-title" v-text="jackpotTypeText" />
                        <div class="entrance__game-jackpot-prize" v-text="asMoney(superJackpot ?? jackpot as number)" />
                    </div>
                </transition>
            </div>
        </transition>
    </div>
</template>

<style lang="scss" scoped>
.entrance {
    width: 100%;
    height: 100%;
    overflow: hidden;
    background: $playgame-bg-color;

    &__background {
        position: fixed;
        width: 100%;
        height: 100%;
        object-fit: cover;
        filter: brightness(50%);
    }

    &__fallback {
        &-background {
            filter: brightness(30%);
            animation: appear 1s $easeOutQuad 1s forwards; // Delayed to prevent flashing before game image load
        }

        &-logo {
            display: inline-block;
            width: 200px;
            height: 200px;
            object-fit: contain;
            opacity: 0;
            animation: appear 0.6s $easeOutQuad 0.1s forwards;
        }
    }

    &__game {
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        min-width: 150px;
        max-width: 200px;
        min-height: 150px;
        text-align: center;

        @media (min-width: $screen-md) {
            max-width: 300px;
            min-width: 200px;
            min-height: 200px;
        }

        &-logo {
            width: auto;
            max-width: 100%;
            height: auto;
            max-height: 100%;
            transform: translateZ(0); // Hack to fix blurry images with decimal dimentions

            // Prevent dragging of image
            -webkit-user-drag: none;
            -khtml-user-drag: none;
            -moz-user-drag: none;
            -o-user-drag: none;
        }

        &-title {
            margin-top: 20px;
            height: 20px;
            color: #fff;
            font-size: $font-size-lg;
            font-weight: $text-bold;
        }

        &-jackpot {
            display: inline-block;
            position: absolute;
            top: -15px;
            right: 0;
            z-index: 2;
            height: 36px;
            padding: 4px 11px;
            background: $gold;
            line-height: 12px;
            text-align: center;
            white-space: nowrap;
            font-weight: $text-heavy;
            border-radius: var(--border-radius, $border-radius);

            &-title {
                color: $gray-darker;
                font-size: $font-size-xxs;
                text-transform: uppercase;
            }

            &-prize {
                color: $red;
            }
        }
    }
}
</style>
